/**
 * Addresses for MegaMek's own files.
 *
 * The art a match draws is MegaMek's, under CC BY-NC-SA 4.0, and it is served
 * rather than committed: one bucket holds a release's `data/` tree verbatim,
 * and the site's own distribution serves it under `/assets/megamek/`. So the
 * address of a file is the path it has inside an install, with a version in
 * front of it:
 *
 *   /assets/megamek/0.51.0/data/images/units/meks/Atlas.png
 *
 * Which is the point of this module. Several screens want a picture for a unit
 * and each of them knows a MegaMek path — from `mekset.txt`, from helm's unit
 * index, or written down here — so the join between "a path in MegaMek" and "a
 * URL this site serves" belongs in one place rather than in each of them.
 *
 * Same-origin, deliberately. These images are drawn onto a canvas and read
 * back pixel by pixel (`camo/mech.ts` is a port of MegaMek's own compositor),
 * and a cross-origin image taints a canvas so that `getImageData` throws. That
 * is also why the address here is a path and never a host: in a browser it
 * resolves against whatever origin the page is on, and the dev server proxies
 * this prefix so that development is same-origin too.
 *
 * `/assets/` is also where Astro emits the site's own hashed build output, but
 * `/assets/megamek/` is carved out of it at the edge and Astro's names are
 * content hashes, so the two cannot collide.
 */

/**
 * The MegaMek whose files are served.
 *
 * arena pins the release a match actually runs — `MEGAMEK_VERSION` in its
 * `versions.env` — and this has to name the same one: a sprite from a
 * different release is a picture of a design as it was in another version,
 * and a battle value from one is not the number the game will give a player.
 *
 * Bumping arena's pin means syncing that release's files
 * (`scripts/assets-megamek.sh` in infra) and then changing this. A version
 * whose files were never synced is a page of broken images, which is why the
 * two steps are in that order.
 *
 * This is a constant for now and should not stay one: helm's unit index is
 * stamped with the release it was built from, so once a screen reads the
 * index, the version comes with the data instead of being repeated here.
 */
export const MEGAMEK_VERSION = "0.51.0";

/**
 * The helm build whose index and rules this site reads.
 *
 * helm's artifacts are keyed by both ids — the wasm is its own code, the
 * index and the catalogue are that code over a MegaMek — so a path names
 * this and the version above together. It has to match `releases.helm` in
 * infra: that is what was published, and a ref that never was is a screen
 * with no designs in it.
 *
 * A constant for the same reason the version above is one, and it should
 * stop being one at the same time: both belong in what a deploy writes
 * rather than in a file somebody edits.
 */
export const HELM_RELEASE = "main-b07e88e78839";

/** Where a release's files are served from. */
const PREFIX = "/assets/megamek";

/**
 * The address of one file inside a MegaMek install.
 *
 * `path` is what it is called in the release — `data/images/units/meks/
 * Atlas.png` — and is not normalised: 6,420 of the 6,995 unit images have a
 * space or a capital in the name, and the bucket is case-sensitive, so a
 * lowercased path is a 404 rather than a picture. Each segment is
 * percent-encoded, and the separators are left alone.
 */
export function megamekAsset(path: string, version = MEGAMEK_VERSION): string {
  const clean = path.replace(/^\/+/, "");
  const encoded = clean.split("/").map(encodeURIComponent).join("/");
  return `${PREFIX}/${encodeURIComponent(version)}/${encoded}`;
}

/**
 * Where helm's index of the unit library lives.
 *
 * Keyed by both ids, because the file is a function of both: the columns are
 * helm's, the library is that MegaMek's. Here rather than beside the loader
 * so that an address is only ever built in one file - the same reason
 * `unitSprite` is here.
 */
export function unitIndexUrl(
  release = HELM_RELEASE,
  version = MEGAMEK_VERSION,
): string {
  return `/assets/helm/${encodeURIComponent(release)}/${encodeURIComponent(version)}/units.json`;
}

/**
 * The address of a unit sprite, named the way `mekset.txt` names it.
 *
 * That file's paths are relative to `data/images/units`, and so is what helm
 * reports, so this is the call almost everything wants.
 */
export function unitSprite(sprite: string, version = MEGAMEK_VERSION): string {
  return megamekAsset(`data/images/units/${sprite}`, version);
}
